import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/api';
import type { RegisterReport } from '../../types/registerReport';
import { athensDateTime } from './registerFormat';

/**
 * Η λογική του παλιού παραθύρου κλεισίματος / Χ (shared/RegisterCloseModal), αυτούσια, για το
 * «Ταμείο»: φόρτωμα της αναφοράς, εκτύπωση Χ, κλείσιμο (με τη λίστα του 409 που το μπλοκάρει),
 * επανεκτύπωση του τελευταίου Ζ, printed=false. Τα endpoints και τα μηνύματα δεν αλλάζουν.
 */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
});

/** Τραπέζι ή παραγγελία σε εξέλιξη που μπλοκάρει το κλείσιμο (409 open_orders). */
export interface OpenItem {
  id: number;
  code: string;
  platform: string;
  order_type?: string;
  status: string;
  total: number;
}

/** Αποτέλεσμα της τελευταίας ενέργειας, στη γραμμή κάτω — όχι alert. */
export type RegisterNotice =
  | { kind: 'x_printed' }
  /** Η Χ δεν μπήκε στην ουρά εκτύπωσης (κανένας ενεργός εκτυπωτής αποδείξεων ή σφάλμα). */
  | { kind: 'x_not_printed' }
  | { kind: 'reprinted'; message: string }
  | { kind: 'error'; message: string; openOrders?: OpenItem[] };

export type RegisterBusy = null | 'x' | 'close' | 'reprint';

/** Αναφορά της ανοιχτής περιόδου (ό,τι θα τύπωνε τώρα η Χ). */
export function useRegisterReport(storeId?: number) {
  const [report, setReport] = useState<RegisterReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  // Και στο StrictMode (mount → unmount → mount) ξαναγίνεται true.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const reload = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/register-report`, { headers: authHeaders() });
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      if (alive.current) setReport(data);
    } catch {
      if (alive.current) setError('Αποτυχία φόρτωσης αναφοράς');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { reload(); }, [reload]);

  return { report, loading, error, reload };
}

/**
 * Εκτύπωση Χ, κλείσιμο ταμείου και επανεκτύπωση. Μία ενέργεια τη φορά (`busy`). Μετά από κλείσιμο
 * που τυπώθηκε, το παράθυρο κλείνει μόνο του σε 1,8″ (`onDone`)· αν ΔΕΝ τυπώθηκε, μένει ανοιχτό με
 * επανεκτύπωση — το κλείσιμο έχει καταγραφεί και δεν επαναλαμβάνεται.
 */
export function useRegisterActions(storeId: number | undefined, onDone: () => void) {
  const [busy, setBusy] = useState<RegisterBusy>(null);
  const [notice, setNotice] = useState<RegisterNotice | null>(null);
  // null = το ταμείο δεν έκλεισε από αυτό το παράθυρο.
  const [closed, setClosed] = useState<{ printed: boolean } | null>(null);
  const inflight = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const run = useCallback(async (kind: Exclude<RegisterBusy, null>, work: () => Promise<void>) => {
    if (!storeId || inflight.current) return;
    inflight.current = true;
    setBusy(kind);
    setNotice(null);
    try {
      await work();
    } catch {
      if (alive.current) setNotice({ kind: 'error', message: 'Αποτυχία σύνδεσης με τον διακομιστή' });
    } finally {
      inflight.current = false;
      if (alive.current) setBusy(null);
    }
  }, [storeId]);

  const post = useCallback(async (path: string, body?: unknown) => {
    const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/${path}`, {
      method: 'POST',
      headers: body === undefined ? authHeaders() : { ...authHeaders(), 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data: (data || {}) as any };
  }, [storeId]);

  /** Χ αναφορά: τυπώνει τα τρέχοντα σύνολα ΧΩΡΙΣ να κλείσει το ταμείο. */
  const printX = useCallback(() => run('x', async () => {
    const { ok, data } = await post('register-x-report');
    if (!alive.current) return;
    if (!ok) {
      setNotice({ kind: 'error', message: data.message || 'Αποτυχία εκτύπωσης Χ αναφοράς' });
      return;
    }
    setNotice({ kind: data.printed === false ? 'x_not_printed' : 'x_printed' });
  }), [run, post]);

  /** Ζ: κλείνει την περίοδο και τυπώνει. Ο server το αρνείται (409) όσο υπάρχουν τραπέζια ή παραγγελίες σε εξέλιξη. */
  const closeRegister = useCallback((trainingClose = false) => run('close', async () => {
    const { ok, data } = await post('register-close', trainingClose ? { trainingClose: true } : undefined);
    if (!alive.current) return;
    if (!ok) {
      setNotice({
        kind: 'error',
        message: data.message || 'Αποτυχία κλεισίματος ταμείου',
        openOrders: data.error === 'open_orders' ? (data.open_orders || []) : undefined,
      });
      return;
    }
    const printed = data.printed !== false;
    setClosed({ printed });
    if (printed) timer.current = setTimeout(() => onDoneRef.current(), 1800);
  }), [run, post]);

  /**
   * Επανεκτύπωση του ΤΕΛΕΥΤΑΙΟΥ κλεισίματος από το αποθηκευμένο snapshot. Δεν κλείνει ταμείο και δεν
   * ξαναϋπολογίζει τίποτα — λύνει το «έκλεισε το ταμείο αλλά δεν βγήκε χαρτί».
   */
  const reprintLast = useCallback(() => run('reprint', async () => {
    const { ok, data } = await post('register-close/reprint', {});
    if (!alive.current) return;
    if (!ok) {
      setNotice({ kind: 'error', message: data.message || 'Αποτυχία επανεκτύπωσης.' });
      return;
    }
    if (data.printed === false) {
      setNotice({
        kind: 'error',
        message: 'Το αντίγραφο δεν στάλθηκε: δεν βρέθηκε ενεργός εκτυπωτής αποδείξεων. Έλεγξε τον εκτυπωτή και δοκίμασε ξανά.',
      });
      return;
    }
    const when = data.closedAt ? athensDateTime(data.closedAt) : '';
    setNotice({ kind: 'reprinted', message: `Στάλθηκε αντίγραφο του κλεισίματος ${when}`.trim() });
    // Κλείσιμο που δεν είχε τυπωθεί: το αντίγραφο βγήκε.
    setClosed(c => (c ? { printed: true } : c));
  }), [run, post]);

  return { busy, notice, closed, printX, closeRegister, reprintLast };
}
